import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useChatContext } from '../context/ChatContext.jsx'
import { useTheme } from '../context/ThemeContext.jsx'
import { useTUI } from '../hooks/useTUI.js'
import FloatingChat from './FloatingChat.jsx'
import ChatPanel from './ChatPanel.jsx'
import TUIOverlay from './TUIOverlay.jsx'
import AboutSiteOverlay from './AboutSiteOverlay.jsx'

const NAV = [
  ['Bio', '/'],
  ['Work', '/experience'],
  ['Education', '/education'],
  ['Projects', '/projects'],
  ['Skills', '/skills'],
  ['Writing', '/blog'],
  ['Contact', '/contact'],
]

function ThemeToggle() {
  const { theme, toggleTheme } = useTheme()
  const isDark = theme === 'dark'
  return (
    <button
      onClick={toggleTheme}
      className="w-[30px] h-[30px] border border-soft-rule flex items-center justify-center text-soft-dim cursor-pointer transition-colors hover:text-soft-ink"
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {isDark ? (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="5" />
          <line x1="12" y1="1" x2="12" y2="3" />
          <line x1="12" y1="21" x2="12" y2="23" />
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
          <line x1="1" y1="12" x2="3" y2="12" />
          <line x1="21" y1="12" x2="23" y2="12" />
          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
          <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
        </svg>
      ) : (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
      )}
    </button>
  )
}

function Chrome({ children }) {
  const location = useLocation()
  const { setPageContext } = useChatContext()
  const { theme } = useTheme()
  const { isOpen: tuiOpen, setIsOpen: setTuiOpen } = useTUI()
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => {
    setPageContext(location.pathname)
    setMenuOpen(false)
  }, [location.pathname, setPageContext])

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden'
      return () => { document.body.style.overflow = '' }
    }
  }, [menuOpen])

  // Page load orchestration — nav links stagger in
  useEffect(() => {
    const navLinks = document.querySelectorAll('.demo-nav .nav-link, nav .nav-link')
    navLinks.forEach((link, i) => {
      link.style.opacity = '0'
      link.style.transform = 'translateY(-8px)'
      setTimeout(() => {
        link.style.transition = 'opacity 0.3s ease, transform 0.3s ease'
        link.style.opacity = '1'
        link.style.transform = 'translateY(0)'
      }, 100 + i * 40)
    })
  }, [])

  return (
    <div data-theme={theme} className="flex flex-col min-h-screen bg-soft-bg text-soft-ink font-sans ab">
      {/* Scroll progress line */}
      <div className="scroll-progress" />
      {/* Grain overlay */}
      <div className="grain-overlay" />
      <header className="px-4 sm:px-14 pt-7 flex justify-between items-center">
        <Link to="/" className="text-[15px] font-medium tracking-tight text-soft-ink no-underline">
          <span className="md:hidden">MJC</span>
          <span className="hidden md:inline">Mugunth J. Chandirasekaran</span>
        </Link>

        {/* Desktop nav + theme toggle */}
        <div className="hidden md:flex items-center gap-[26px]">
          <nav className="flex gap-[26px] text-sm" aria-label="Main navigation">
            {NAV.map(([name, path]) => {
              const isActive = path === '/' ? location.pathname === path : location.pathname.startsWith(path)
              return (
                <Link
                  key={name}
                  to={path}
                  className={`nav-link no-underline transition-colors ${
                    isActive ? 'text-soft-ink' : 'text-soft-dim hover:text-soft-ink'
                  }`}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {name}
                </Link>
              )
            })}
          </nav>
          <div className="ml-[18px]">
            <ThemeToggle />
          </div>
        </div>

        {/* Mobile menu button */}
        <button
          className="md:hidden text-sm text-soft-dim cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
        >
          {menuOpen ? 'Close' : 'Menu'}
        </button>
      </header>

      {/* Mobile drawer */}
      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-50 bg-soft-bg flex flex-col items-center justify-center gap-8">
          <button
            onClick={() => setMenuOpen(false)}
            className="absolute top-6 right-6 text-soft-dim hover:text-soft-ink cursor-pointer text-2xl leading-none"
            aria-label="Close menu"
          >
            ×
          </button>
          <div className="absolute top-7 left-6">
            <ThemeToggle />
          </div>
          {NAV.map(([name, path]) => {
            const isActive = path === '/' ? location.pathname === path : location.pathname.startsWith(path)
            return (
              <Link
                key={name}
                to={path}
                className={`nav-link text-xl no-underline transition-colors ${
                  isActive ? 'text-soft-ink' : 'text-soft-dim'
                }`}
                onClick={() => setMenuOpen(false)}
              >
                {name}
              </Link>
            )
          })}
        </div>
      )}

      <main className="flex-1">{children}</main>

      <footer className="px-4 sm:px-14 pt-12 pb-6 text-xs text-soft-faint flex gap-2 items-center">
        <span>&copy; 2026 Mugunth J. Chandirasekaran</span>
        <span>·</span>
        <span>Dublin</span>
        <Link
          to="/stats"
          aria-label="(easter egg)"
          className="cursor-help text-soft-faint hover:text-soft-dim transition-colors no-print"
        >
          ·
        </Link>
      </footer>

      <FloatingChat />
      <ChatPanel />
      {tuiOpen && <TUIOverlay onClose={() => setTuiOpen(false)} />}
      <AboutSiteOverlay />
    </div>
  )
}

export default Chrome
